import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CiEdit } from 'react-icons/ci';
import { fetchJewellersSidebarRequest } from '../../../redux/JewellerList/JewellerSlice';
import {
  getInstalaxmiPopupsRequest,
  saveInstalaxmiPopupRequest,
  updateInstalaxmiPopupStatusRequest,
} from '../../../redux/LuckyDrawPopup/luckyDrawPopupSlice';
import TableCompo from '../Table/TableCompo';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import useCsvConverter from '../../../hooks/convertToCSV';

const formatDate = (value) =>
  value ? moment(value).format('DD-MM-YYYY hh:mm A') : '-';

const userName = (user) => {
  if (!user) return '-';
  if (typeof user === 'string') return user;
  return user.name || '-';
};

const triggerCsvDownload = (csvText, filename) => {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const LuckyDrawPopup = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const { jewellersSidebar = [] } = useSelector((state) => state.jeweller);
  const cdnUrl = useSelector(
    (state) => state?.appConfig?.configData?.cdnUrl || '',
  );
  const {
    popupData = [],
    total = 0,
    loading,
    saving,
    statusUpdatingId,
    saveVersion = 0,
  } = useSelector((state) => state.luckyDrawPopup || {});

  const [targetType, setTargetType] = useState('All');
  const [selectedJewellers, setSelectedJewellers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileNames, setFileNames] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [jewellersOpen, setJewellersOpen] = useState(false);
  const [jewellersList, setJewellersList] = useState([]);
  const [jewellerSearch, setJewellerSearch] = useState('');
  const seenSaveVersion = useRef(0);
  const previewUrlsRef = useRef([]);

  const listParams = {
    page: page + 1,
    limit,
    ...(search ? { search } : {}),
  };

  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest({ allActive: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getInstalaxmiPopupsRequest(listParams));
  }, [dispatch, page, limit, search]);

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const resetForm = () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setEditId(null);
    setTargetType('All');
    setSelectedJewellers([]);
    setSelectedFiles([]);
    setFileNames([]);
    setPreviewUrls([]);
    setExistingFiles([]);
  };

  useEffect(() => {
    if (!saveVersion || seenSaveVersion.current === saveVersion) return;
    seenSaveVersion.current = saveVersion;
    resetForm();
  }, [saveVersion]);

  const jewellerOptions = [...jewellersSidebar].sort((a, b) =>
    (a.jewellerName || '').localeCompare(b.jewellerName || ''),
  );

  const resolveFileUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = (cdnUrl || process.env.REACT_APP_API_BASE_URL || '').replace(
      /\/$/,
      '',
    );
    return `${base}/${String(path).replace(/^\//, '')}`;
  };

  const jewellerLabel = (value) => {
    const fromSidebar = jewellersSidebar.find((item) => item.id === value);
    if (fromSidebar?.jewellerName) return fromSidebar.jewellerName;
    const editRow = popupData.find((item) => item._id === editId);
    const fromRow = (editRow?.jewellers || []).find(
      (item) => item._id === value,
    );
    return fromRow?.jewellerName || value;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.some((file) => !file.type.startsWith('image/'))) {
      toast.error('Please select a valid image file');
      return;
    }
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    setSelectedFiles(files);
    setFileNames(files.map((file) => file.name));
    setPreviewUrls(files.map((file) => URL.createObjectURL(file)));
  };

  const handleSave = () => {
    if (targetType === 'Custom' && !selectedJewellers.length) {
      toast.error('Please select jeweller');
      return;
    }
    if (!editId && !selectedFiles.length) {
      toast.error('Please choose a file before saving');
      return;
    }

    const current = popupData.find((item) => item._id === editId);
    const body = {
      type: targetType,
      status: editId ? !!current?.status : true,
      files: selectedFiles,
      ...(editId ? { id: editId } : {}),
      ...(targetType === 'Custom' ? { jewellerIds: selectedJewellers } : {}),
    };

    dispatch(
      saveInstalaxmiPopupRequest({
        body,
        listParams: editId ? listParams : { ...listParams, page: 1 },
      }),
    );
    if (!editId) setPage(0);
  };

  const handleEdit = (row) => {
    setEditId(row._id);
    setTargetType(row.type === 'Custom' ? 'Custom' : 'All');
    setSelectedJewellers(
      (Array.isArray(row.jewellerIds) ? row.jewellerIds : [])
        .map((item) => (typeof item === 'string' ? item : item?._id))
        .filter(Boolean),
    );
    setSelectedFiles([]);
    setFileNames([]);
    setPreviewUrls([]);
    setExistingFiles(Array.isArray(row.files) ? row.files : []);
  };

  const handleStatusToggle = (row) => {
    dispatch(
      updateInstalaxmiPopupStatusRequest({
        id: row._id,
        status: !row.status,
        listParams,
      }),
    );
  };

  const handleDownloadCsv = () => {
    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/jeweller-lucky-draw/getInstalaxmiPopupsCsv',
        query: {},
        callback: (response) => {
          if (response?.error) {
            toast.error(response.message || 'Failed to download CSV');
            return;
          }
          const rows = Array.isArray(response?.data)
            ? response.data
            : response?.data?.data || [];
          const csv = convertToCsv(rows);
          if (!csv) {
            toast.error('No records to download');
            return;
          }
          triggerCsvDownload(csv, 'instalaxmi-popups.csv');
        },
      }),
    );
  };

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPage(tableState.page);
        break;
      case 'changeRowsPerPage':
        setLimit(tableState.rowsPerPage);
        setPage(0);
        break;
      case 'search':
        setSearch(tableState.searchText || '');
        setPage(0);
        break;
      default:
        break;
    }
  };

  const tableRows = popupData.map((row) => ({
    ...row,
    jewellerText:
      row.type === 'All'
        ? 'All'
        : (row.jewellers || [])
            .map((item) => item.jewellerName)
            .filter(Boolean)
            .join(', ') || '-',
    createdByName: userName(row.createdBy),
    updatedByName: userName(row.updatedBy),
    createdAtText: formatDate(row.createdAt),
    updatedAtText: formatDate(row.updatedAt),
  }));

  const columns = [
    { field: 'createdAtText', headerName: 'Created At', minWidth: 170 },
    { field: 'type', headerName: 'Type', minWidth: 110 },
    {
      field: 'jewellerText',
      headerName: 'Jeweller',
      minWidth: 140,
      sortable: false,
      render: (_, row) => {
        const jewellers = row.jewellers || [];
        if (row.type === 'All') return 'All';
        if (!jewellers.length) return '-';
        return (
          <button
            style={{
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={() => {
              setJewellersList(jewellers);
              setJewellerSearch('');
              setJewellersOpen(true);
            }}
          >
            View
          </button>
        );
      },
    },
    {
      field: 'files',
      headerName: 'Image',
      minWidth: 160,
      hideInExcel: true,
      sortable: false,
      render: (files) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {(files || []).map((file) => (
            <img
              key={file}
              src={resolveFileUrl(file)}
              alt=""
              onClick={() => {
                setImageUrl(resolveFileUrl(file));
                setImageOpen(true);
              }}
              style={{
                width: 48,
                height: 48,
                objectFit: 'cover',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      hideInExcel: true,
      sortable: false,
      render: (status, row) => {
        const enabled = !!status;
        return (
          <Button
            variant="contained"
            size="small"
            disabled={statusUpdatingId === row._id}
            onClick={() => handleStatusToggle(row)}
            sx={{
              backgroundColor: enabled ? 'green' : '#d32f2f',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: enabled ? '#1b5e20' : '#b71c1c',
              },
            }}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </Button>
        );
      },
    },
    { field: 'createdByName', headerName: 'Created By', minWidth: 150 },
    { field: 'updatedByName', headerName: 'Updated By', minWidth: 150 },
    { field: 'updatedAtText', headerName: 'Updated At', minWidth: 170 },
    {
      field: 'edit',
      headerName: 'Edit',
      minWidth: 90,
      hideInExcel: true,
      sortable: false,
      render: (_, row) => (
        <CiEdit
          style={{ cursor: 'pointer', fontSize: '22px' }}
          onClick={() => handleEdit(row)}
        />
      ),
    },
  ];

  const shownPreviews = previewUrls.length
    ? previewUrls
    : existingFiles.map(resolveFileUrl);

  const popupForm = (
    <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl>
            <RadioGroup
              row
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value);
                if (e.target.value === 'All') setSelectedJewellers([]);
              }}
            >
              <FormControlLabel value="All" control={<Radio />} label="All" />
              <FormControlLabel
                value="Custom"
                control={<Radio />}
                label="Custom"
              />
            </RadioGroup>
          </FormControl>

          {targetType === 'Custom' && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={[
                ...jewellerOptions,
                ...selectedJewellers
                  .filter((id) => !jewellerOptions.some((item) => item.id === id))
                  .map((id) => ({ id, jewellerName: jewellerLabel(id) })),
              ]}
              getOptionLabel={(option) => option?.jewellerName || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedJewellers.map((id) => {
                const jeweller = jewellerOptions.find((item) => item.id === id);
                if (jeweller) return jeweller;
                return { id, jewellerName: jewellerLabel(id) };
              })}
              onChange={(_, newValue) =>
                setSelectedJewellers(newValue.map((item) => item.id))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Jeweller"
                  placeholder="Search jeweller"
                />
              )}
              sx={{ minWidth: 280, width: 320 }}
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2} mt={3} flexWrap="wrap">
          <input
            type="file"
            accept="image/*"
            multiple
            onClick={(e) => {
              e.target.value = null;
            }}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="lucky-draw-popup-image"
          />
          <label htmlFor="lucky-draw-popup-image">
            <Button variant="outlined" component="span">
              Choose File
            </Button>
          </label>
          <Typography variant="body2">
            {fileNames.length
              ? fileNames.join(', ')
              : editId
                ? 'Choose files only if you want to replace the images'
                : ''}
          </Typography>
        </Box>
        {shownPreviews.length > 0 && (
          <Box mt={2} mb={2} display="flex" gap={1} flexWrap="wrap">
            {shownPreviews.map((url) => (
              <img
                key={url}
                src={url}
                alt="Preview"
                style={{ width: 100, height: 'auto' }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            sx={{ bgcolor: '#752ADA', px: 6, textTransform: 'none' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : editId ? 'Update' : 'Save'}
          </Button>
          {editId && (
            <Button
              variant="outlined"
              sx={{ px: 4, textTransform: 'none' }}
              onClick={resetForm}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
        </Box>
    </>
  );

  return (
    <Box sx={{ width: '100%', pr: 2, boxSizing: 'border-box' }}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          width: '100%',
          border: '1px solid #ccc',
          boxSizing: 'border-box',
        }}
      >
        <Typography fontWeight={700} fontSize={24} mb={3} fontFamily={'Aeonik'}>
          Lucky Draw Popup
        </Typography>
        {!editId && popupForm}
      </Paper>

      <Box
        sx={{
          mt: 3,
          pb: 3,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <Typography fontWeight={700} fontSize={24} fontFamily={'Aeonik'}>
          Lucky Draw Popup list
        </Typography>
        <TableCompo
          title="Lucky Draw Popup list"
          columns={columns}
          data={tableRows}
          totalCount={total}
          page={page}
          limit={limit}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onTableChange={handleTableChange}
          extraButtons={
            <Button size="small" variant="outlined" onClick={handleDownloadCsv}>
              Download CSV
            </Button>
          }
        />
      </Box>

      <Dialog
        open={jewellersOpen}
        onClose={() => setJewellersOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Jewellers ({jewellersList.length})</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            placeholder="Search jeweller"
            value={jewellerSearch}
            onChange={(e) => setJewellerSearch(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {jewellersList
              .filter((item) =>
                (item.jewellerName || '')
                  .toLowerCase()
                  .includes(jewellerSearch.toLowerCase()),
              )
              .map((item, index) => (
                <Typography
                  key={item._id || index}
                  variant="body2"
                  sx={{ py: 1, borderBottom: '1px solid #eee' }}
                >
                  {index + 1}. {item.jewellerName}
                </Typography>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJewellersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editId}
        onClose={resetForm}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: 24, fontFamily: 'Aeonik', pb: 0 }}
        >
          Lucky Draw Popup
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>{editId ? popupForm : null}</DialogContent>
      </Dialog>

      <Dialog open={imageOpen} onClose={() => setImageOpen(false)} maxWidth="md">
        <DialogTitle>Popup Image</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          <img
            src={imageUrl}
            alt="Popup"
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              borderRadius: 8,
              objectFit: 'contain',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LuckyDrawPopup;
